import {
  SummaryDetails,
  SummarySectionData
} from 'components/FlowSummarySection/FlowSummarySection';
import { StateDataContext } from 'containers/Common/StateDataContext';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import { OrderType } from 'core/order/Order';
import { get, isEmpty, omitBy } from 'lodash';
import { LocaleMeta } from 'core';
import { LimitationInventorySettings } from 'containers/Limitations/LimitationSetting/limitationConfig/limitationSettingsType';
import { SummaryTitleColor } from 'components/SummaryDetail/SummaryDetail';
import i18n from 'i18n';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { OPERATES } from 'enum/Operate';
import {
  ColumnDefinition,
  getComponentColumn,
  getNameIdColumn
} from 'components/TableColumn/TableColumn';
import { RtbCampaignContext } from './LimitationManagementContext';

export type LimitationSummaryStateContentModel = {
  title: string;
  limitationSummaryTitle: string;
  limitationSummaryData?: SummarySectionData;
  ignoreCampaignHint: string;
  ignoreCampaignSummaryTableData: {
    data: {
      id: number;
      name: string;
      limitationSummaryData: SummarySectionData;
    }[];
    columns: ColumnDefinition[];
  };
};

const defaultRtbCampaignManager: RtbCampaignManager =
  new DefaultRtbCampaignManager();

const getLimitationSummaryData = (
  limitations: LimitationData,
  limitationInventorySettings: LimitationInventorySettings[],
  supportAgeGenderLimitation: boolean,
  localeMeta?: LocaleMeta
) => {
  const limitationSummaryData =
    defaultRtbCampaignManager.getLimitationSummaryData(
      limitations,
      OrderType.TENMAX,
      get(localeMeta, 'property.addonProps.agencySegmentLimitationName', ''),
      limitationInventorySettings
        .filter((setting) => setting.hideLimitation)
        .map((setting) => setting.name)
        .concat(supportAgeGenderLimitation ? [] : ['age_min', 'age_max'])
    );
  return omitBy(
    {
      include: {
        title: i18n.t('campaignSummary.titles.inc'),
        titlePrefixColor: SummaryTitleColor.GREEN,
        content: limitationSummaryData.include
      },
      preferred: {
        title: i18n.t('campaignSummary.titles.preferred'),
        titlePrefixColor: SummaryTitleColor.BLUE,
        content: limitationSummaryData.preferred
      },
      nonPreferred: {
        title: i18n.t('campaignSummary.titles.nonPreferred'),
        titlePrefixColor: SummaryTitleColor.YELLOW,
        content: limitationSummaryData.nonPreferred
      },
      exclude: {
        title: i18n.t('campaignSummary.titles.exc'),
        titlePrefixColor: SummaryTitleColor.RED,
        content: limitationSummaryData.exclude
      },
      other: limitationSummaryData.other
        ? {
            title: i18n.t('campaignSummary.titles.other'),
            content: limitationSummaryData.other
          }
        : undefined
    },
    isEmpty
  ) as SummarySectionData;
};

const ignoreCampaignTableColumns = [
  getNameIdColumn({
    sort: false,
    text: i18n.t('ignoreCampaignSummary.headers.name'),
    dataField: 'id'
  }),
  getComponentColumn(
    {
      sort: false,
      text: i18n.t('ignoreCampaignSummary.headers.limitation'),
      dataField: 'limitationSummaryData'
    },
    SummaryDetails,
    (data) => ({
      summaryData: data.limitationSummaryData
    })
  )
];

const useIgnoreCampaignSummaryTableData = (
  ignoreCampaigns: RtbCampaignContext[],
  limitationInventorySettings: LimitationInventorySettings[],
  supportAgeGenderLimitation: boolean,
  localeMeta?: LocaleMeta
) => {
  const ignoreCampaignSummaryData = useMemo(() => {
    return ignoreCampaigns.map((campaign) => {
      const limitationData: LimitationData = campaign.limitations;
      const limitationSummaryData = getLimitationSummaryData(
        limitationData,
        limitationInventorySettings,
        supportAgeGenderLimitation,
        localeMeta
      );
      return {
        id: campaign.id,
        name: campaign.name,
        limitationSummaryData
      };
    });
  }, [
    ignoreCampaigns,
    limitationInventorySettings,
    supportAgeGenderLimitation,
    localeMeta
  ]);

  const ignoreCampaignSummaryTableData = useMemo(() => {
    return {
      data: ignoreCampaignSummaryData,
      columns: ignoreCampaignTableColumns
    };
  }, [ignoreCampaignSummaryData]);

  return ignoreCampaignSummaryTableData;
};

export const useAddLimitationSummaryStateContentModel = (
  campaigns: RtbCampaignContext[],
  limitationInventorySettings: LimitationInventorySettings[],
  supportAgeGenderLimitation: boolean,
  localeMeta?: LocaleMeta
): LimitationSummaryStateContentModel => {
  const { data, setData } = useContext(StateDataContext);

  const { limitations } = data;

  const limitationSummaryData = getLimitationSummaryData(
    limitations,
    limitationInventorySettings,
    supportAgeGenderLimitation,
    localeMeta
  );

  const isCampaignLimitationConflict = useCallback(
    (
      operation: OPERATES,
      limitation: Limitation,
      campaignLimitations: LimitationData
    ) => {
      const { type, value: limitationValue } = limitation;
      const ignoreTypes = ['unicornlanguage', 'age_min', 'age_max', 'gender'];
      if (ignoreTypes.includes(type)) {
        return false;
      }

      const valuesToAdd = Array.isArray(limitationValue)
        ? limitationValue.map((value) => value.value)
        : [limitationValue];

      return Object.keys(campaignLimitations).some(
        (campaignLimitationOperation) => {
          if (campaignLimitationOperation === operation) {
            return false;
          }
          const limitationsOfOperationAndType = campaignLimitations[
            campaignLimitationOperation as OPERATES
          ].find((limitation) => limitation.type === type);
          const valuesOfOperationAndType: SelectOptions[] | number | string =
            get(limitationsOfOperationAndType, 'value', []);
          const valuesCannotRepeat = Array.isArray(valuesOfOperationAndType)
            ? valuesOfOperationAndType.map((limitation) => limitation.value)
            : [valuesOfOperationAndType];
          return !!valuesCannotRepeat.find((valueCannotRepeat) =>
            valuesToAdd.includes(valueCannotRepeat)
          );
        }
      );
    },
    []
  );

  const ignoreCampaigns = useMemo(() => {
    const limitationData: LimitationData = limitations;
    return campaigns.filter((campaign) =>
      Object.keys(limitationData).some((operation) =>
        limitationData[operation].some((limitation) =>
          isCampaignLimitationConflict(
            operation as OPERATES,
            limitation,
            campaign.limitations
          )
        )
      )
    );
  }, [campaigns, limitations, isCampaignLimitationConflict]);

  useEffect(() => {
    setData({
      limitations,
      campaignIds: campaigns
        .filter(
          (campaign) =>
            !ignoreCampaigns.find((conflict) => conflict.id === campaign.id)
        )
        .map((campaign) => campaign.id)
    });
  }, [ignoreCampaigns, campaigns, limitations, setData]);

  const ignoreCampaignSummaryTableData = useIgnoreCampaignSummaryTableData(
    ignoreCampaigns,
    limitationInventorySettings,
    supportAgeGenderLimitation,
    localeMeta
  );

  return {
    title: i18n.t('modifyLimitationState.titles.add'),
    limitationSummaryTitle: i18n.t('limitationSummaryState.titles.add'),
    limitationSummaryData,
    ignoreCampaignHint: i18n.t(
      'modifyLimitationState.hints.ignoreAddToCampaigns'
    ),
    ignoreCampaignSummaryTableData
  };
};

export const useRemoveLimitationSummaryStateContentModel = (
  campaigns: RtbCampaignContext[],
  limitationInventorySettings: LimitationInventorySettings[],
  supportAgeGenderLimitation: boolean,
  localeMeta?: LocaleMeta
): LimitationSummaryStateContentModel => {
  const { data } = useContext(StateDataContext);

  const { limitations } = data;
  const limitationSummaryData = getLimitationSummaryData(
    limitations,
    limitationInventorySettings,
    supportAgeGenderLimitation,
    localeMeta
  );

  const ignoreCampaigns = useMemo(() => {
    const limitationData: LimitationData = limitations;
    return campaigns.filter((campaign) => {
      const limitationIncludeToRemove = limitationData.include;
      const adxLimitationToRemove = limitationIncludeToRemove.find(
        (limitation) => limitation.type === 'adx'
      );
      const originAdxLimitation = campaign.limitations.include.find(
        (limitation) => limitation.type === 'adx'
      );
      if (
        adxLimitationToRemove &&
        Array.isArray(adxLimitationToRemove.value) &&
        originAdxLimitation &&
        Array.isArray(originAdxLimitation.value)
      ) {
        return (
          originAdxLimitation.value.length -
            adxLimitationToRemove.value.length <=
          0
        );
      }
      return false;
    });
  }, [campaigns, limitations]);

  const ignoreCampaignSummaryTableData = useIgnoreCampaignSummaryTableData(
    ignoreCampaigns,
    limitationInventorySettings,
    supportAgeGenderLimitation,
    localeMeta
  );

  return {
    title: i18n.t('modifyLimitationState.titles.remove'),
    limitationSummaryTitle: i18n.t('limitationSummaryState.titles.remove'),
    limitationSummaryData,
    ignoreCampaignHint: i18n.t(
      'modifyLimitationState.hints.ignoreRemoveFromCampaigns'
    ),
    ignoreCampaignSummaryTableData
  };
};
